import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth, useTheme, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { 
  ArrowLeft,
  Play,
  Pause,
  ChevronLeft,
  ChevronRight,
  Settings,
  Sun,
  Moon,
  Minus,
  Plus,
  Volume2,
  Mic,
  MicOff,
  BookOpen,
  Feather,
  Mountain,
  Loader2,
  Headphones,
  Users,
  UserCheck,
  RefreshCw,
  CheckCircle2,
  Star,
  ArrowDown
} from "lucide-react";
import { KITT } from "@/constants/hedgekit";

// Bouncing arrow indicator component for current word
const WordIndicator = ({ isVisible }) => {
  if (!isVisible) return null;
  return (
    <span className="absolute -top-7 left-1/2 -translate-x-1/2 text-amber-500 animate-word-arrow z-10 pointer-events-none">
      <ArrowDown className="w-6 h-6 drop-shadow-lg stroke-[3]" />
    </span>
  );
};

const BANDS = {
  "short-simple": { name: "Short & Simple", icon: Feather, color: "bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300" },
  "regular-chapter": { name: "Regular Chapter", icon: BookOpen, color: "bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300" },
  "big-challenge": { name: "Big Challenge", icon: Mountain, color: "bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300" }
};

// Reading modes
const READING_MODES = {
  LISTEN: 'listen',      // App reads to student
  TOGETHER: 'together',  // Echo reading - app reads, student repeats
  STUDENT: 'student'     // Student reads, app listens
};

// TTS Service - Uses Native Browser Web Speech API exclusively (no external API calls)
class TTSService {
  constructor() {
    this.synth = window.speechSynthesis;
    this.voices = [];
    this.currentVoice = null;
    this.isReady = false;
    this.isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    this.isPlaying = false;
    
    // Load voices
    this.loadVoices();
    this.synth.onvoiceschanged = () => this.loadVoices();
    
    console.log('TTS Service: Browser Web Speech API (free, no credits used)');
  }

  loadVoices() {
    this.voices = this.synth.getVoices().filter(v => v.lang.startsWith('en'));
    this.currentVoice = this.voices.find(v => 
      v.name.includes('Samantha') || v.name.includes('Google') || v.default
    ) || this.voices[0];
    this.isReady = !!this.currentVoice;
  }

  // ===== WORD CLICK: 3-Step Pronunciation Sequence =====
  async speakWordClick(word, onComplete) {
    // Clean the word
    const cleanWord = word.replace(/[^\w'-]/g, '');
    if (!cleanWord) return;
    
    // Syllable map for common words (expand as needed)
    const syllableMap = {
      'winnie': 'win-nie',
      'pooh': 'pooh',
      'piglet': 'pig-let',
      'tigger': 'tig-ger',
      'rabbit': 'rab-bit',
      'eeyore': 'ee-yore',
      'christopher': 'chris-to-pher',
      'robin': 'rob-in',
      'hundred': 'hun-dred',
      'forest': 'for-est',
      'adventure': 'ad-ven-ture',
      'wonderful': 'won-der-ful',
      'beautiful': 'beau-ti-ful',
      'everyone': 'ev-ry-one',
      'something': 'some-thing',
      'nothing': 'noth-ing',
      'everything': 'ev-ry-thing',
      'princess': 'prin-cess',
      'golden': 'gold-en',
      'castle': 'cas-tle',
      'dragon': 'drag-on',
      'magic': 'mag-ic',
      'fairy': 'fair-y',
      'little': 'lit-tle',
      'mother': 'moth-er',
      'father': 'fa-ther',
      'brother': 'broth-er',
      'sister': 'sis-ter',
      'children': 'chil-dren',
      'animal': 'an-i-mal',
      'elephant': 'el-e-phant',
      'together': 'to-geth-er',
      'remember': 're-mem-ber',
      'different': 'dif-fer-ent',
      'important': 'im-por-tant',
    };
    
    const hyphenated = syllableMap[cleanWord.toLowerCase()] || this.autoSyllabify(cleanWord);
    
    try {
      // Step 1: Sound Out - Full word at 0.4 speed
      await this.speakWithWebSpeechDirect(cleanWord, 0.4);
      await this.delay(400);
      
      // Step 2: Syllabize - Hyphenated at 0.5 speed with pauses
      const syllables = hyphenated.split('-');
      for (let i = 0; i < syllables.length; i++) {
        await this.speakWithWebSpeechDirect(syllables[i], 0.5);
        if (i < syllables.length - 1) {
          await this.delay(300);
        }
      }
      await this.delay(400);
      
      // Step 3: Natural Repeat - Full word at 0.75 speed
      await this.speakWithWebSpeechDirect(cleanWord, 0.75);
      
      onComplete?.();
    } catch (e) {
      console.error('Word click TTS error:', e);
      onComplete?.();
    }
  }
  
  // Auto-syllabify for words not in the map (basic algorithm)
  autoSyllabify(word) {
    const vowels = 'aeiouy';
    const result = [];
    let current = '';
    
    for (let i = 0; i < word.length; i++) {
      current += word[i];
      const isVowel = vowels.includes(word[i].toLowerCase());
      const nextIsConsonant = i + 1 < word.length && !vowels.includes(word[i + 1].toLowerCase());
      
      if (isVowel && nextIsConsonant && current.length >= 2) {
        result.push(current);
        current = '';
      }
    }
    
    if (current) {
      if (result.length > 0 && current.length <= 2) {
        result[result.length - 1] += current;
      } else {
        result.push(current);
      }
    }
    
    return result.length > 1 ? result.join('-') : word;
  }
  
  // Direct Web Speech for word clicks (synchronous feel)
  speakWithWebSpeechDirect(text, rate) {
    return new Promise((resolve) => {
      if (!this.synth || !this.currentVoice) {
        resolve();
        return;
      }
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.voice = this.currentVoice;
      utterance.rate = rate;
      utterance.pitch = 1.0;
      utterance.onend = () => resolve();
      utterance.onerror = () => resolve();
      
      this.synth.cancel();
      this.synth.speak(utterance);
    });
  }
  
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
  // ===== END WORD CLICK =====

  async speak(text, { rate = 0.9, onEnd } = {}) {
    this.stop();
    
    // Use Web Speech API exclusively (free, no credits)
    return this.speakWithWebSpeechChunked(text, onEnd);
  }
  
  // Chunked Web Speech for mobile - breaks long text into smaller pieces
  async speakWithWebSpeechChunked(text, onEnd) {
    // On mobile, break into chunks of ~10 words max to prevent cutoff
    const words = text.split(' ');
    const chunkSize = this.isMobile ? 10 : 50;
    const chunks = [];
    
    for (let i = 0; i < words.length; i += chunkSize) {
      chunks.push(words.slice(i, i + chunkSize).join(' '));
    }
    
    console.log(`TTS (Web Speech chunked): ${chunks.length} chunks`);
    
    for (let i = 0; i < chunks.length; i++) {
      await this.speakChunk(chunks[i]);
      // Small pause between chunks
      if (i < chunks.length - 1) {
        await new Promise(r => setTimeout(r, 100));
      }
    }
    
    onEnd?.();
  }
  
  speakChunk(text) {
    return new Promise((resolve) => {
      if (!this.currentVoice) this.loadVoices();
      
      const utterance = new SpeechSynthesisUtterance(text);
      if (this.currentVoice) utterance.voice = this.currentVoice;
      utterance.rate = this.isMobile ? 0.85 : 0.9;
      utterance.pitch = 1.1;
      
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        resolve();
      };
      
      utterance.onend = finish;
      utterance.onerror = finish;
      
      // Timeout fallback
      const timeout = setTimeout(finish, (text.split(' ').length / 2) * 1000 + 2000);
      utterance.onend = () => { clearTimeout(timeout); finish(); };
      
      this.synth.cancel();
      this.synth.speak(utterance);
    });
  }
  
  speakWithWebSpeech(text, rate, onEnd) {
    return new Promise((resolve) => {
      if (!this.currentVoice) {
        this.loadVoices();
      }
      
      const utterance = new SpeechSynthesisUtterance(text);
      if (this.currentVoice) {
        utterance.voice = this.currentVoice;
      }
      utterance.rate = rate;
      utterance.pitch = 1.1;
      
      console.log('TTS (Web Speech):', text.substring(0, 50) + '...');
      
      let hasEnded = false;
      const handleEnd = () => {
        if (hasEnded) return;
        hasEnded = true;
        console.log('TTS finished (Web Speech)');
        onEnd?.();
        resolve();
      };
      
      utterance.onend = handleEnd;
      utterance.onerror = handleEnd;
      
      // Fallback timeout
      const timeout = setTimeout(() => {
        if (!hasEnded && !this.synth.speaking) {
          handleEnd();
        }
      }, (text.split(' ').length / 2) * 1000 + 2000);
      
      utterance.onend = () => {
        clearTimeout(timeout);
        handleEnd();
      };
      
      this.synth.cancel();
      setTimeout(() => this.synth.speak(utterance), 50);
    });
  }

  speakWord(word) {
    this.stop();
    // Always use Web Speech for single words (faster)
    const utterance = new SpeechSynthesisUtterance(word);
    if (this.currentVoice) {
      utterance.voice = this.currentVoice;
    }
    utterance.rate = 0.8;
    this.synth.speak(utterance);
  }

  stop() {
    // Stop Web Speech
    this.synth.cancel();
  }

  pause() {
    this.synth.pause();
  }

  resume() {
    this.synth.resume();
  }

  isSpeaking() {
    return this.synth.speaking;
  }
}

// Speech Recognition Service - Robust for mobile and desktop
class SpeechRecognitionService {
  constructor() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    this.isSupported = !!SpeechRecognition;
    this.recognition = null;
    this.isListening = false;
    this.onResult = null;
    this.transcript = '';
    this.hasPermission = false;
    this.restartCount = 0;
    
    if (this.isSupported) {
      this.initRecognition();
    }
  }
  
  initRecognition() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    this.recognition = new SpeechRecognition();
    this.recognition.continuous = true;
    this.recognition.interimResults = true;
    this.recognition.lang = 'en-US';
    
    this.recognition.onresult = (event) => {
      let transcript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      if (transcript) {
        this.transcript = transcript;
        console.log('STT heard:', transcript);
        this.onResult?.(transcript, event.results[event.results.length - 1]?.isFinal || false);
      }
    };
    
    this.recognition.onerror = (event) => {
      console.log('STT error:', event.error);
      if (event.error === 'not-allowed') {
        this.hasPermission = false;
        this.isListening = false;
        alert('Microphone blocked. Please allow microphone access in your browser settings.');
      } else if (this.isListening && this.restartCount < 5) {
        // Auto-restart on other errors
        this.restartCount++;
        setTimeout(() => this.safeRestart(), 300);
      }
    };
    
    this.recognition.onend = () => {
      console.log('STT ended, isListening:', this.isListening);
      if (this.isListening && this.restartCount < 5) {
        this.restartCount++;
        setTimeout(() => this.safeRestart(), 100);
      }
    };
    
    this.recognition.onstart = () => {
      console.log('STT started successfully');
      this.restartCount = 0; // Reset on successful start
    };
  }
  
  safeRestart() {
    if (!this.isListening || !this.recognition) return;
    try {
      this.recognition.start();
    } catch (e) {
      // If already started, that's fine
      if (!e.message?.includes('already started')) {
        console.log('STT restart error:', e.message);
      }
    }
  }

  async requestPermission() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      this.hasPermission = true;
      return true;
    } catch (e) {
      console.error('Mic permission denied:', e);
      this.hasPermission = false;
      return false;
    }
  }

  async start(onResult, onEnd) {
    if (!this.isSupported) {
      alert('Speech recognition is not supported in this browser. Please use Chrome or Safari.');
      return false;
    }
    
    // Request permission first
    if (!this.hasPermission) {
      const granted = await this.requestPermission();
      if (!granted) {
        alert('Microphone access is required. Please allow and try again.');
        return false;
      }
    }
    
    // Recreate recognition object (fixes some mobile issues)
    this.initRecognition();
    
    this.onResult = onResult;
    this.transcript = '';
    this.isListening = true;
    this.restartCount = 0;
    
    try {
      this.recognition.start();
      console.log('STT starting...');
      return true;
    } catch (e) {
      console.error('STT start failed:', e);
      return false;
    }
  }

  stop() {
    console.log('STT stopping');
    this.isListening = false;
    this.restartCount = 5; // Prevent restarts
    try {
      this.recognition?.stop();
    } catch (e) {}
  }

  forceRestart() {
    if (!this.isListening) return;
    console.log('STT force restart');
    this.restartCount = 0;
    try {
      this.recognition?.stop();
    } catch (e) {}
    setTimeout(() => {
      if (this.isListening) {
        this.initRecognition();
        try {
          this.recognition.start();
        } catch (e) {
          console.error('Force restart failed:', e);
        }
      }
    }, 300);
  }
}

const tts = new TTSService();
const stt = new SpeechRecognitionService();

// Encouraging messages
const ENCOURAGEMENTS = [
  "Great reading!",
  "You're doing amazing!",
  "Wonderful job!",
  "Keep it up!",
  "Fantastic!",
  "You're a star reader!",
  "Excellent work!",
  "That was perfect!"
];

const getRandomEncouragement = () => 
  ENCOURAGEMENTS[Math.floor(Math.random() * ENCOURAGEMENTS.length)];

export default function Reader() {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  const [book, setBook] = useState(location.state?.book || null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const band = location.state?.band || "short-simple";
  
  // Reading mode - default to "Read With Me" (TOGETHER)
  const [readingMode, setReadingMode] = useState(READING_MODES.TOGETHER);
  
  // Reading state
  const [paragraphs, setParagraphs] = useState([]);
  const [currentParagraph, setCurrentParagraph] = useState(0);
  const [currentSentenceIdx, setCurrentSentenceIdx] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [highlightedWord, setHighlightedWord] = useState(null);
  const [speechRate, setSpeechRate] = useState(0.9);
  
  // Access/Paywall state
  const [hasAccess, setHasAccess] = useState(null); // null = checking, true/false = result
  const [accessInfo, setAccessInfo] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [licenseKey, setLicenseKey] = useState('');
  const [verifyingLicense, setVerifyingLicense] = useState(false);
  
  // Student reading state
  const [studentTranscript, setStudentTranscript] = useState('');
  const [wordsRead, setWordsRead] = useState([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [sentencesCompleted, setSentencesCompleted] = useState(0);
  const [showEncouragement, setShowEncouragement] = useState(false);
  const [encouragementText, setEncouragementText] = useState('');
  const [showModeUnlockCelebration, setShowModeUnlockCelebration] = useState(false);
  const [prevModesUnlocked, setPrevModesUnlocked] = useState(false);
  
  // Together mode state
  const [togetherPhase, setTogetherPhase] = useState('listen');
  
  // Learning Mode TTS state (word click 3-step pronunciation)
  const [learningWord, setLearningWord] = useState(null);
  
  // Mode unlocking - "Read With Me" is always available
  // "Read to Me" and "I'll Read" unlock after 5 sentences in "Read With Me"
  const SENTENCES_TO_UNLOCK = 5;
  const [togetherSentencesRead, setTogetherSentencesRead] = useState(0);
  const modesUnlocked = togetherSentencesRead >= SENTENCES_TO_UNLOCK;
  
  // Detect when modes get unlocked and show celebration
  useEffect(() => {
    if (modesUnlocked && !prevModesUnlocked) {
      setShowModeUnlockCelebration(true);
      setTimeout(() => setShowModeUnlockCelebration(false), 3500);
    }
    setPrevModesUnlocked(modesUnlocked);
  }, [modesUnlocked, prevModesUnlocked]);
  
  // Refs for tracking state in callbacks (avoids stale closure issues)
  const isPlayingRef = useRef(false);
  const paragraphsRef = useRef([]);
  const currentParagraphRef = useRef(0);
  const currentSentenceIdxRef = useRef(0);
  const readingModeRef = useRef(READING_MODES.TOGETHER); // Default to Together mode
  const speechRateRef = useRef(0.9);
  const wordsReadRef = useRef([]);
  const currentWordIndexRef = useRef(0);
  const isCompletingRef = useRef(false); // Prevent double-completion
  
  // Keep refs in sync with state
  useEffect(() => { paragraphsRef.current = paragraphs; }, [paragraphs]);
  useEffect(() => { currentParagraphRef.current = currentParagraph; }, [currentParagraph]);
  useEffect(() => { currentSentenceIdxRef.current = currentSentenceIdx; }, [currentSentenceIdx]);
  useEffect(() => { readingModeRef.current = readingMode; }, [readingMode]);
  useEffect(() => { speechRateRef.current = speechRate; }, [speechRate]);
  useEffect(() => { wordsReadRef.current = wordsRead; }, [wordsRead]);
  useEffect(() => { currentWordIndexRef.current = currentWordIndex; }, [currentWordIndex]);
  
  // Settings
  const [fontSize, setFontSize] = useState(22);
  const [lineHeight, setLineHeight] = useState(2.2);
  const [showSettings, setShowSettings] = useState(false);
  
  // Comprehension
  const [showComprehension, setShowComprehension] = useState(false);
  const [comprehensionResponse, setComprehensionResponse] = useState("");
  
  const contentRef = useRef(null);
  const paragraphRefs = useRef({});
  const readingStartTime = useRef(null);
  const totalReadingMinutes = useRef(0);

  // Split paragraph into sentences for better display
  const getSentences = (paragraph) => {
    if (!paragraph) return [];
    
    // If paragraph is very short (like "CHAPTER I"), treat it as a single sentence
    const trimmed = paragraph.trim();
    if (trimmed.length < 50 && !trimmed.includes('.')) {
      return [trimmed];
    }
    
    // Split on sentence-ending punctuation
    const sentences = paragraph
      .split(/(?<=[.!?])\s+/)
      .filter(s => s.trim().length > 0);
    
    // If no sentences found (no punctuation), return the whole paragraph
    if (sentences.length === 0) {
      return [trimmed];
    }
    
    return sentences;
  };
  
  // Clean text for reading - removes asterisks, underscores, etc.
  const cleanTextForReading = (text) => {
    if (!text) return '';
    let cleaned = text;
    // Remove asterisk dividers
    cleaned = cleaned.replace(/\*\s*\*\s*\*[\s*]*/g, '');
    cleaned = cleaned.replace(/\*+/g, '');
    // Remove underscores
    cleaned = cleaned.replace(/_/g, '');
    return cleaned.trim();
  };
  
  // Check if paragraph is just a divider (asterisks, dashes, etc.)
  const isDividerParagraph = (para) => {
    if (!para) return true;
    const cleaned = para.replace(/[\s\*\-_=]/g, '');
    return cleaned.length === 0;
  };
  
  // Get current sentence text - uses REFS for callbacks
  const getCurrentSentenceFromRefs = () => {
    const paras = paragraphsRef.current;
    const paraIdx = currentParagraphRef.current;
    const sentIdx = currentSentenceIdxRef.current;
    
    // Safety checks
    if (!paras || paras.length === 0) return '';
    if (paraIdx < 0 || paraIdx >= paras.length) return '';
    if (!paras[paraIdx]) return '';
    
    // Skip divider paragraphs
    if (isDividerParagraph(paras[paraIdx])) return '';
    
    const sentences = getSentences(paras[paraIdx]);
    let sentence = sentences[sentIdx] || sentences[0] || '';
    
    // Clean up the sentence
    sentence = cleanTextForReading(sentence);
    return sentence;
  };
  
  // Convert text for natural TTS speech (Roman numerals, asterisks, etc.)
  const convertTextForSpeech = (text) => {
    let result = text;
    
    // Remove asterisk dividers (e.g., "* * * * *" or "***")
    result = result.replace(/\*\s*\*\s*\*[\s*]*/g, '');
    result = result.replace(/\*+/g, '');
    
    // Roman numeral to word mapping
    const romanToWord = {
      'XII': 'twelve', 'XI': 'eleven', 'X': 'ten',
      'IX': 'nine', 'VIII': 'eight', 'VII': 'seven',
      'VI': 'six', 'V': 'five', 'IV': 'four',
      'III': 'three', 'II': 'two', 'I': 'one'
    };
    
    // Replace Roman numerals after "Chapter" or "CHAPTER"
    for (const [roman, word] of Object.entries(romanToWord)) {
      result = result.replace(new RegExp(`(Chapter\\s+)${roman}\\b`, 'gi'), `$1${word}`);
    }
    
    // Replace standalone Roman numerals (like "I" at start or "I." for chapter)
    for (const [roman, word] of Object.entries(romanToWord)) {
      // At start of text
      result = result.replace(new RegExp(`^${roman}\\b`, 'i'), word);
      // After newline
      result = result.replace(new RegExp(`\\n${roman}\\b`, 'gi'), `\n${word}`);
    }
    
    return result.trim();
  };
  
  // Legacy function name for compatibility
  const convertRomanNumeralsForSpeech = convertTextForSpeech;
  
  // Get current sentence text - uses STATE for rendering
  const getCurrentSentence = () => {
    if (paragraphs.length === 0) return '';
    
    // Skip divider paragraphs
    if (isDividerParagraph(paragraphs[currentParagraph])) return '';
    
    const sentences = getSentences(paragraphs[currentParagraph]);
    let sentence = sentences[currentSentenceIdx] || sentences[0] || '';
    
    // Clean up the sentence
    sentence = cleanTextForReading(sentence);
    return sentence;
  };
  
  // Get words from current sentence for tracking - uses REFS for callbacks
  const getCurrentSentenceWordsFromRefs = () => {
    const sentence = getCurrentSentenceFromRefs();
    return sentence
      .split(/\s+/)
      .map(w => w.replace(/[^\w'-]/g, '').toLowerCase())
      .filter(w => w.length > 0);
  };
  
  // Get words from current sentence for tracking - uses STATE for rendering
  const getCurrentSentenceWords = () => {
    return getCurrentSentence()
      .split(/\s+/)
      .map(w => w.replace(/[^\w'-]/g, '').toLowerCase())
      .filter(w => w.length > 0);
  };

  // Check access before loading book
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const token = localStorage.getItem('kbn_session_token');
        const res = await fetch(`${API}/access/check`, { 
          credentials: 'include',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (res.ok) {
          const data = await res.json();
          setAccessInfo(data);
          setHasAccess(data.valid);
          if (!data.valid) {
            setShowPaywall(true);
          }
        } else {
          setHasAccess(false);
          setShowPaywall(true);
        }
      } catch (e) {
        console.error('Access check failed:', e);
        setHasAccess(false);
        setShowPaywall(true);
      }
    };
    
    if (user) {
      checkAccess();
    }
  }, [user]);

  // Verify license key
  const verifyLicense = async () => {
    if (!licenseKey.trim()) {
      toast.error('Please enter your license key');
      return;
    }
    
    setVerifyingLicense(true);
    try {
      const token = localStorage.getItem('kbn_session_token');
      const res = await fetch(`${API}/access/verify-license`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({ license_key: licenseKey.trim() })
      });
      
      const data = await res.json();
      
      if (data.valid) {
        setAccessInfo(data);
        setHasAccess(true);
        setShowPaywall(false);
        toast.success(data.message);
      } else {
        toast.error(data.message || 'Invalid license key');
      }
    } catch (e) {
      toast.error('Failed to verify license. Please try again.');
    } finally {
      setVerifyingLicense(false);
    }
  };

  // Fetch book details and text
  useEffect(() => {
    const fetchBook = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Determine if this is a local story (string ID like "pg1597") or Gutenberg (numeric)
        const isLocalStory = isNaN(parseInt(bookId));
        
        // Fetch book details if not in state
        if (!book) {
          const endpoint = isLocalStory 
            ? `${API}/local-stories/${bookId}` 
            : `${API}/books/${bookId}`;
          const detailsRes = await fetch(endpoint, { credentials: 'include' });
          if (!detailsRes.ok) throw new Error('Book not found');
          const details = await detailsRes.json();
          setBook(details);
        }
        
        // Fetch book text
        const textEndpoint = isLocalStory
          ? `${API}/local-stories/${bookId}/text`
          : `${API}/books/${bookId}/text`;
        const textRes = await fetch(textEndpoint, { credentials: 'include' });
        if (!textRes.ok) throw new Error('Failed to load book text');
        const textData = await textRes.json();
        setText(textData.text);
        
        // Split into paragraphs
        const paras = textData.text
          .split(/\n\n+/)
          .map(p => p.trim())
          .filter(p => p.length > 0);
        setParagraphs(paras);
        
        // Load saved progress
        try {
          const token = localStorage.getItem('kbn_session_token');
          const progressRes = await fetch(`${API}/progress/${bookId}`, { 
            credentials: 'include',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });
          if (progressRes.ok) {
            const progress = await progressRes.json();
            console.log('Loaded progress:', progress);
            // Use paragraph_index if available, otherwise fall back to position
            if (typeof progress.paragraph_index === 'number') {
              const safeParaIdx = Math.max(0, Math.min(progress.paragraph_index, paras.length - 1));
              setCurrentParagraph(safeParaIdx);
              
              // Validate sentence_index against actual sentences in the paragraph
              const sentences = paras[safeParaIdx] ? 
                paras[safeParaIdx].split(/(?<=[.!?])\s+/).filter(s => s.trim().length > 0) : [];
              const maxSentIdx = Math.max(0, sentences.length - 1);
              const safeSentIdx = Math.max(0, Math.min(progress.sentence_index || 0, maxSentIdx));
              setCurrentSentenceIdx(safeSentIdx);
              
              console.log(`Progress loaded: para ${safeParaIdx}/${paras.length}, sent ${safeSentIdx}/${sentences.length}`);
            } else if (progress.position) {
              // Legacy: Find paragraph at position
              let charCount = 0;
              for (let i = 0; i < paras.length; i++) {
                charCount += paras[i].length + 2;
                if (charCount >= progress.position) {
                  setCurrentParagraph(Math.max(0, i));
                  break;
                }
              }
            }
          }
        } catch (e) {
          console.log('No saved progress or error loading:', e);
        }
      } catch (err) {
        console.error('Error loading book:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBook();
    
    // Start reading timer
    readingStartTime.current = Date.now();
    
    return () => {
      tts.stop();
      // Log reading time when leaving
      logReadingTime();
    };
  }, [bookId, book]);

  // Log reading time to gamification system
  const logReadingTime = async (completedBook = false) => {
    if (!readingStartTime.current) return;
    
    const elapsedMs = Date.now() - readingStartTime.current;
    const elapsedMinutes = Math.floor(elapsedMs / 60000);
    
    if (elapsedMinutes > 0) {
      try {
        const token = localStorage.getItem('kbn_session_token');
        const response = await fetch(`${API}/gamification/log-reading`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          credentials: 'include',
          body: JSON.stringify({
            minutes_read: elapsedMinutes,
            book_id: parseInt(bookId),
            completed_book: completedBook
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          // Show achievement toast if new achievements earned
          if (data.new_achievements && data.new_achievements.length > 0) {
            data.new_achievements.forEach(name => {
              toast.success(`Achievement unlocked: ${name}!`, { duration: 4000 });
            });
          }
        }
      } catch (e) {
        console.error('Failed to log reading time:', e);
      }
      
      // Reset timer
      readingStartTime.current = Date.now();
      totalReadingMinutes.current += elapsedMinutes;
    }
  };

  // Scroll to current paragraph - triggers on paragraph change AND on initial load
  useEffect(() => {
    if (paragraphRefs.current[currentParagraph]) {
      // Small delay to ensure DOM is ready after initial load
      const timer = setTimeout(() => {
        paragraphRefs.current[currentParagraph]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [currentParagraph, paragraphs.length]); // Also trigger when paragraphs load

  // Save progress periodically
  const saveProgress = useCallback(async () => {
    if (!book || paragraphs.length === 0) return;
    
    try {
      const token = localStorage.getItem('kbn_session_token');
      await fetch(`${API}/progress`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({
          book_id: parseInt(bookId),
          book_title: book.title,
          book_author: book.author,
          paragraph_index: currentParagraph,
          sentence_index: currentSentenceIdx,
          band
        })
      });
      console.log('Progress saved:', currentParagraph, currentSentenceIdx);
    } catch (e) {
      console.error('Failed to save progress:', e);
    }
  }, [bookId, book, currentParagraph, currentSentenceIdx, paragraphs, band]);

  // Save progress on paragraph/sentence change
  useEffect(() => {
    if (book && paragraphs.length > 0 && (currentParagraph > 0 || currentSentenceIdx > 0)) {
      saveProgress();
    }
  }, [currentParagraph, currentSentenceIdx, saveProgress, book, paragraphs.length]);

  // Auto-save progress periodically and on page unload
  useEffect(() => {
    const progressInterval = setInterval(saveProgress, 15000); // Every 15 seconds
    const readingInterval = setInterval(() => logReadingTime(false), 300000); // Every 5 minutes
    
    // Save on page unload
    const handleUnload = () => {
      if (book && paragraphs.length > 0) {
        navigator.sendBeacon?.(`${API}/progress`, JSON.stringify({
          book_id: parseInt(bookId),
          book_title: book.title,
          book_author: book.author,
          paragraph_index: currentParagraph,
          sentence_index: currentSentenceIdx,
          band
        }));
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    window.addEventListener('pagehide', handleUnload);
    
    return () => {
      clearInterval(progressInterval);
      clearInterval(readingInterval);
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('pagehide', handleUnload);
    };
  }, [saveProgress, book, bookId, currentParagraph, currentSentenceIdx, paragraphs.length, band]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      tts.stop();
      stt.stop();
    };
  }, []);

  // ===== MODE-SPECIFIC FUNCTIONS =====

  // Show encouragement
  const showEncouragementMessage = () => {
    setEncouragementText(getRandomEncouragement());
    setShowEncouragement(true);
    setTimeout(() => setShowEncouragement(false), 2000);
  };

  // Move to next sentence
  // Move to next sentence - updates both state AND refs
  // Also skips divider paragraphs (asterisks, etc.)
  const moveToNextSentence = () => {
    const paras = paragraphsRef.current;
    let paraIdx = currentParagraphRef.current;
    const sentIdx = currentSentenceIdxRef.current;
    
    const sentences = getSentences(paras[paraIdx]);
    
    if (sentIdx < sentences.length - 1) {
      // Next sentence in same paragraph
      const newSentIdx = sentIdx + 1;
      currentSentenceIdxRef.current = newSentIdx;
      setCurrentSentenceIdx(newSentIdx);
      // Reset word tracking - BOTH state AND refs
      setCurrentWordIndex(0);
      setWordsRead([]);
      setStudentTranscript('');
      wordsReadRef.current = [];
      currentWordIndexRef.current = 0;
    } else {
      // Move to next paragraph - skip any divider paragraphs
      let newParaIdx = paraIdx + 1;
      
      // Skip divider paragraphs (asterisks, dashes, empty, etc.)
      while (newParaIdx < paras.length && isDividerParagraph(paras[newParaIdx])) {
        console.log('Skipping divider paragraph:', newParaIdx);
        newParaIdx++;
      }
      
      if (newParaIdx < paras.length) {
        currentParagraphRef.current = newParaIdx;
        currentSentenceIdxRef.current = 0;
        setCurrentParagraph(newParaIdx);
        setCurrentSentenceIdx(0);
        // Reset word tracking - BOTH state AND refs
        setCurrentWordIndex(0);
        setWordsRead([]);
        setStudentTranscript('');
        wordsReadRef.current = [];
        currentWordIndexRef.current = 0;
        
        // Note: Encouragement is only shown in active modes (Read With Me, I'll Read)
        // "Read to Me" (passive listening) doesn't get encouragement popups
      } else {
        // End of book
        setIsPlaying(false);
        setIsListening(false);
        isPlayingRef.current = false;
        stt.stop();
        setShowComprehension(true);
      }
    }
  };

  // LISTEN MODE: App reads to student
  const startListenMode = async () => {
    setIsPlaying(true);
    isPlayingRef.current = true;
    speakNextSentence();
  };

  // Speak using refs (not stale state)
  const speakNextSentence = () => {
    if (!isPlayingRef.current) return;
    
    let sentence = getCurrentSentenceFromRefs();
    
    // If sentence is empty (divider paragraph), skip to next
    if (!sentence || sentence.trim().length === 0) {
      console.log('Empty sentence, moving to next...');
      moveToNextSentence();
      setTimeout(() => {
        if (isPlayingRef.current) {
          speakNextSentence();
        }
      }, 100);
      return;
    }
    
    // Convert Roman numerals and clean for TTS
    sentence = convertTextForSpeech(sentence);
    
    // Cancel any stuck speech first
    tts.stop();
    
    setTimeout(async () => {
      if (!isPlayingRef.current) return;
      
      await tts.speak(sentence, {
        rate: speechRateRef.current,
        onEnd: () => {
          if (isPlayingRef.current && readingModeRef.current === READING_MODES.LISTEN) {
            moveToNextSentence();
            // Small delay then speak next
            setTimeout(() => {
              if (isPlayingRef.current) {
                speakNextSentence();
              }
            }, 350);
          }
        }
      });
    }, 100);
  };

  // Legacy function name for compatibility
  const speakCurrentSentence = speakNextSentence;

  // TOGETHER MODE: App reads, then student repeats
  const startTogetherMode = async () => {
    setIsPlaying(true);
    isPlayingRef.current = true;
    setTogetherPhase('listen');
    
    // Stop any listening first
    stt.stop();
    setIsListening(false);
    
    // Prompt student
    await tts.speak("Listen carefully, then repeat after me.", { rate: 0.9 });
    await new Promise(r => setTimeout(r, 300));
    
    if (isPlayingRef.current) {
      speakForTogether();
    }
  };

  const speakForTogether = async () => {
    if (!isPlayingRef.current) return;
    
    let sentence = getCurrentSentenceFromRefs();
    
    // If sentence is empty (divider paragraph), skip to next
    if (!sentence || sentence.trim().length === 0) {
      console.log('Empty sentence in Together mode, moving to next...');
      moveToNextSentence();
      setTimeout(() => speakForTogether(), 100);
      return;
    }
    
    // Convert for natural speech
    sentence = convertTextForSpeech(sentence);
    
    setTogetherPhase('listen');
    
    // Make sure we're not listening while TTS speaks
    stt.stop();
    setIsListening(false);
    
    console.log('TTS speaking:', sentence.substring(0, 50) + '...');
    
    // Use await to ensure TTS completes
    await tts.speak(sentence, { rate: speechRateRef.current * 0.9 });
    
    // Check if we're still playing
    if (!isPlayingRef.current) return;
    
    console.log('TTS finished, prompting student');
    
    // Now it's student's turn
    setTogetherPhase('repeat');
    await tts.speak("Now you try!", { rate: 1.0 });
    
    // Wait for audio to settle
    await new Promise(r => setTimeout(r, 800));
    
    if (isPlayingRef.current) {
      await startStudentListening();
    }
  };

  // STUDENT MODE: Student reads, app listens
  const startStudentMode = async () => {
    setIsPlaying(true);
    isPlayingRef.current = true;
    
    // Make sure STT is stopped before we speak the prompt
    stt.stop();
    setIsListening(false);
    
    // Voice prompt
    await tts.speak("Your turn to read! I'll follow along.", { rate: 0.9 });
    
    // Wait for TTS to settle
    await new Promise(r => setTimeout(r, 800));
    
    if (isPlayingRef.current) {
      await startStudentListening();
    }
  };

  const startStudentListening = async () => {
    // Reset word tracking state AND refs
    setIsListening(true);
    setWordsRead([]);
    setCurrentWordIndex(0);
    setStudentTranscript('');
    wordsReadRef.current = [];
    currentWordIndexRef.current = 0;
    isCompletingRef.current = false; // Reset completion guard
    
    const started = await stt.start(
      (transcript, isFinal) => {
        setStudentTranscript(transcript);
        checkStudentProgress(transcript);
      },
      () => {
        // Speech ended
      }
    );
    
    if (!started) {
      setIsListening(false);
      setIsPlaying(false);
      isPlayingRef.current = false;
    }
  };

  const stopStudentListening = () => {
    setIsListening(false);
    stt.stop();
  };

  // Number/word mappings for flexible matching
  const numberWordMap = {
    '0': ['zero', 'oh', 'o'],
    '1': ['one', 'won', 'i'],
    '2': ['two', 'to', 'too', 'ii'],
    '3': ['three', 'iii'],
    '4': ['four', 'for', 'iv'],
    '5': ['five', 'v'],
    '6': ['six', 'vi'],
    '7': ['seven', 'vii'],
    '8': ['eight', 'ate', 'viii'],
    '9': ['nine', 'ix'],
    '10': ['ten', 'x'],
    '11': ['eleven', 'xi'],
    '12': ['twelve', 'xii'],
    // Roman numerals reverse lookup
    'i': ['one', '1', 'eye', 'aye'],
    'ii': ['two', '2'],
    'iii': ['three', '3'],
    'iv': ['four', '4'],
    'v': ['five', '5'],
    'vi': ['six', '6'],
    'vii': ['seven', '7'],
    'viii': ['eight', '8'],
    'ix': ['nine', '9'],
    'x': ['ten', '10'],
    'xi': ['eleven', '11'],
    'xii': ['twelve', '12'],
  };
  
  // Check if two words match with flexible rules
  const wordsMatch = (spoken, target) => {
    const s = spoken.replace(/[^\w]/g, '').toLowerCase();
    const t = target.replace(/[^\w]/g, '').toLowerCase();
    
    // Exact match
    if (s === t) return true;
    
    // One contains the other (for contractions, etc.)
    if (s.length > 2 && t.length > 2) {
      if (s.includes(t) || t.includes(s)) return true;
    }
    
    // Number/Roman numeral matching
    if (numberWordMap[t]) {
      if (numberWordMap[t].includes(s)) return true;
    }
    if (numberWordMap[s]) {
      if (numberWordMap[s].includes(t)) return true;
    }
    
    // Single letter matching - speech recognition often hears single letters
    // e.g., "bee" heard as "b", "see" heard as "c", "tea" heard as "t"
    const letterSounds = {
      'b': ['bee', 'be'],
      'c': ['see', 'sea'],
      'd': ['dee'],
      'e': ['ee'],
      'g': ['gee'],
      'i': ['eye', 'aye'],
      'j': ['jay'],
      'k': ['kay'],
      'o': ['oh'],
      'p': ['pee', 'pea'],
      'q': ['queue', 'cue'],
      'r': ['are'],
      't': ['tea', 'tee'],
      'u': ['you'],
      'y': ['why'],
    };
    
    // Check if spoken single letter matches target word sound
    if (s.length === 1 && letterSounds[s]) {
      if (letterSounds[s].includes(t)) return true;
    }
    // Check if target is single letter and spoken matches its sound
    if (t.length === 1 && letterSounds[t]) {
      if (letterSounds[t].includes(s)) return true;
    }
    // Also check reverse - if target is "bee" and spoken is "b"
    for (const [letter, sounds] of Object.entries(letterSounds)) {
      if (sounds.includes(t) && s === letter) return true;
      if (sounds.includes(s) && t === letter) return true;
    }
    
    // Fuzzy match for longer words (allow 2 character difference)
    if (s.length > 3 && t.length > 3) {
      const distance = levenshteinDistance(s, t);
      const maxAllowedDistance = Math.min(2, Math.floor(Math.max(s.length, t.length) / 3));
      if (distance <= maxAllowedDistance) return true;
    }
    
    // Common homophones and variations
    const homophones = {
      'the': ['da', 'duh', 'thee'],
      'a': ['uh', 'ay'],
      'to': ['two', '2', 'too'],
      'for': ['four', '4', 'fore'],
      'there': ['their', 'theyre'],
      'your': ['youre', 'ur'],
      'its': ['its'],
      'and': ['an', 'n', 'end'],
      'bee': ['b', 'be'],
      'sea': ['c', 'see'],
      'tea': ['t', 'tee'],
      'pea': ['p', 'pee'],
      'are': ['r'],
      'you': ['u'],
      'why': ['y'],
      'oh': ['o'],
      'eye': ['i'],
    };
    
    if (homophones[t] && homophones[t].includes(s)) return true;
    if (homophones[s] && homophones[s].includes(t)) return true;
    
    return false;
  };

  // Check if student is reading the words correctly
  const checkStudentProgress = (transcript) => {
    // CRITICAL: Use ref-based function to get current sentence words
    // This ensures we're checking against the RIGHT sentence, not stale state
    const targetWords = getCurrentSentenceWordsFromRefs();
    const spokenWords = transcript.toLowerCase().split(/\s+/).filter(w => w.length > 0);
    
    // Use ref to get current matched words (avoid stale closure)
    let matchedWords = [...wordsReadRef.current];
    let wordIdx = matchedWords.length; // Start from where we left off
    
    console.log('Target words:', targetWords);
    console.log('Spoken words:', spokenWords);
    console.log('Starting from word index:', wordIdx);
    
    // Try to match each spoken word to the next expected word
    for (const spoken of spokenWords) {
      if (wordIdx < targetWords.length) {
        const target = targetWords[wordIdx];
        
        if (wordsMatch(spoken, target)) {
          console.log(`✓ Matched: "${spoken}" → "${target}"`);
          if (!matchedWords.includes(wordIdx)) {
            matchedWords.push(wordIdx);
          }
          wordIdx++;
        } else {
          console.log(`✗ No match: "${spoken}" vs "${target}"`);
          // Try to match with the next word (skip if student skipped a word)
          if (wordIdx + 1 < targetWords.length && wordsMatch(spoken, targetWords[wordIdx + 1])) {
            console.log(`⟳ Skipped ahead: matched "${spoken}" → "${targetWords[wordIdx + 1]}"`);
            matchedWords.push(wordIdx); // Mark skipped word as matched too (be lenient)
            matchedWords.push(wordIdx + 1);
            wordIdx += 2;
          }
        }
      }
    }
    
    setWordsRead(matchedWords);
    setCurrentWordIndex(wordIdx);
    
    console.log('Matched so far:', matchedWords.length, '/', targetWords.length);
    
    // Check if sentence is complete - require ALL words to be matched
    // This prevents cutting off sentences early
    // ALSO check isCompletingRef to prevent double-triggering
    if (matchedWords.length >= targetWords.length && !isCompletingRef.current) {
      // Set the guard IMMEDIATELY to prevent any more completion triggers
      isCompletingRef.current = true;
      
      // Student completed the sentence!
      console.log('🎉 Sentence complete!');
      stopStudentListening();
      
      // Log the sentence completion to backend (only for active modes)
      const currentMode = readingModeRef.current;
      if (currentMode === READING_MODES.TOGETHER || currentMode === READING_MODES.STUDENT) {
        const modeKey = currentMode === READING_MODES.TOGETHER ? 'together' : 'student';
        logSentenceToBackend(1, modeKey);
        
        // Track together sentences for mode unlocking
        if (currentMode === READING_MODES.TOGETHER) {
          setTogetherSentencesRead(prev => {
            const newCount = prev + 1;
            // Show Kitt encouragement every 5 sentences (earned praise)
            if (newCount % 5 === 0) {
              showEncouragementMessage();
            }
            return newCount;
          });
        } else {
          // For student mode, also track and show every 5
          setSentencesCompleted(prev => {
            const newCount = prev + 1;
            if (newCount % 5 === 0) {
              showEncouragementMessage();
            }
            return newCount;
          });
        }
      }
      
      setTimeout(() => {
        // Reset the guard and move to next
        isCompletingRef.current = false;
        
        if (readingModeRef.current === READING_MODES.TOGETHER) {
          // In together mode, move to next and repeat cycle
          moveToNextSentence();
          setTimeout(() => speakForTogether(), 500);
        } else {
          // In student mode, move to next
          moveToNextSentence();
          setTimeout(() => startStudentListening(), 500);
        }
      }, 1500);
    }
  };
  
  // Log sentence completion to backend
  const logSentenceToBackend = async (sentences, mode) => {
    try {
      const token = localStorage.getItem('kbn_session_token');
      await fetch(`${API}/gamification/log-reading`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({
          sentences_read: sentences,
          mode: mode,
          book_id: parseInt(bookId),
          completed_book: false
        })
      });
    } catch (error) {
      console.error('Failed to log reading:', error);
    }
  };

  // Simple Levenshtein distance for fuzzy matching
  const levenshteinDistance = (a, b) => {
    if (a.length === 0) return b.length;
    if (b.length === 0) return a.length;
    
    const matrix = [];
    for (let i = 0; i <= b.length; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= a.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    return matrix[b.length][a.length];
  };

  // Stop all playback and listening - used when switching modes
  const stopEverything = () => {
    tts.stop();
    stt.stop();
    setIsPlaying(false);
    setIsListening(false);
    isPlayingRef.current = false;
    isCompletingRef.current = false;
  };

  // Switch reading mode with cleanup
  const switchMode = (newMode) => {
    if (newMode === readingMode) return;
    
    // Stop current mode's activity
    stopEverything();
    
    // Reset word tracking
    setWordsRead([]);
    setCurrentWordIndex(0);
    setStudentTranscript('');
    wordsReadRef.current = [];
    currentWordIndexRef.current = 0;
    
    // Set new mode
    setReadingMode(newMode);
  };

  // Main toggle function
  const togglePlay = async () => {
    if (isPlaying || isListening) {
      // Stop everything
      stopEverything();
    } else {
      // Start based on mode
      isPlayingRef.current = true;
      switch (readingMode) {
        case READING_MODES.LISTEN:
          await startListenMode();
          break;
        case READING_MODES.TOGETHER:
          await startTogetherMode();
          break;
        case READING_MODES.STUDENT:
          await startStudentMode();
          break;
        default:
          await startListenMode();
      }
    }
  };

  // Legacy function for backward compatibility
  const speakCurrentParagraph = async () => {
    await startListenMode();
  };

  // Navigation
  const goToPrevious = () => {
    tts.stop();
    stt.stop();
    setIsPlaying(false);
    setIsListening(false);
    isPlayingRef.current = false;
    
    const sentences = getSentences(paragraphs[currentParagraph]);
    if (currentSentenceIdx > 0) {
      setCurrentSentenceIdx(prev => prev - 1);
    } else if (currentParagraph > 0) {
      setCurrentParagraph(prev => prev - 1);
      const prevSentences = getSentences(paragraphs[currentParagraph - 1]);
      setCurrentSentenceIdx(prevSentences.length - 1);
    }
    setCurrentWordIndex(0);
    setWordsRead([]);
  };

  const goToNext = () => {
    tts.stop();
    stt.stop();
    setIsPlaying(false);
    setIsListening(false);
    isPlayingRef.current = false;
    moveToNextSentence();
  };

  // Word click handler with Learning Mode TTS (3-step sequence)
  const handleWordClick = async (word, e) => {
    e.stopPropagation();
    const cleanWord = word.replace(/[^\w'-]/g, '');
    if (!cleanWord) return;
    
    // Prevent clicking another word while one is being pronounced
    if (learningWord) return;
    
    // Stop any ongoing playback/listening
    tts.stop();
    
    // Show visual feedback
    setLearningWord(cleanWord);
    toast(`Learning: "${cleanWord}"`, { 
      duration: 4000,
      icon: '🔊'
    });
    
    // Run the 3-step Learning Mode TTS sequence
    await tts.speakWordClick(cleanWord, () => {
      setLearningWord(null);
    });
  };

  // Save comprehension response
  const saveComprehension = async () => {
    if (!comprehensionResponse.trim()) {
      setShowComprehension(false);
      return;
    }
    
    try {
      const token = localStorage.getItem('kbn_session_token');
      await fetch(`${API}/comprehension`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({
          book_id: parseInt(bookId),
          chapter_or_section: `Paragraph ${currentParagraph + 1}`,
          response: comprehensionResponse
        })
      });
      toast.success("Great thinking! Your response has been saved.");
    } catch (e) {
      console.error('Failed to save comprehension:', e);
    }
    
    setComprehensionResponse("");
    setShowComprehension(false);
  };

  // Render paragraph with sentences and word highlighting for reading modes
  const renderParagraph = (paragraphText, paragraphIndex) => {
    const sentences = getSentences(paragraphText);
    const isCurrentParagraph = paragraphIndex === currentParagraph;
    
    return sentences.map((sentence, sentenceIndex) => {
      const isCurrentSentence = isCurrentParagraph && sentenceIndex === currentSentenceIdx;
      const words = sentence.split(/(\s+)/);
      let realWordIndex = 0;
      
      return (
        <span 
          key={sentenceIndex}
          className={`transition-all duration-300 ${
            isCurrentSentence
              ? 'bg-primary/15 rounded-lg px-2 py-1 -mx-2 inline' 
              : isCurrentParagraph ? '' : ''
          }`}
        >
          {words.map((word, wordIndex) => {
            if (/^\s+$/.test(word)) {
              return <span key={`${sentenceIndex}-${wordIndex}`}>{word}</span>;
            }
            
            const thisWordIndex = realWordIndex;
            realWordIndex++;
            
            // Check if this word has been read by student
            const isWordRead = isCurrentSentence && wordsRead.includes(thisWordIndex);
            const isCurrentWord = isCurrentSentence && thisWordIndex === currentWordIndex && (readingMode === READING_MODES.STUDENT || readingMode === READING_MODES.TOGETHER);
            const showArrow = isCurrentWord && (isListening || isPlaying);
            
            return (
              <span
                key={`${sentenceIndex}-${wordIndex}`}
                onClick={(e) => handleWordClick(word, e)}
                className={`cursor-pointer transition-all duration-200 px-0.5 -mx-0.5 rounded relative inline-block ${
                  isWordRead 
                    ? 'bg-green-300 dark:bg-green-700 text-green-900 dark:text-green-100 font-semibold' 
                    : isCurrentWord
                      ? 'bg-amber-300 dark:bg-amber-600 text-amber-900 dark:text-amber-100 font-bold scale-110 shadow-md ring-2 ring-amber-400 dark:ring-amber-500'
                      : 'hover:bg-primary/20'
                }`}
                data-testid={`word-${paragraphIndex}-${sentenceIndex}-${wordIndex}`}
              >
                <WordIndicator isVisible={showArrow} />
                {word}
              </span>
            );
          })}
          {sentenceIndex < sentences.length - 1 && ' '}
        </span>
      );
    });
  };

  const bandInfo = BANDS[band] || BANDS["short-simple"];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <img 
            src={KITT.reading} 
            alt="Kitt reading" 
            className="w-32 h-32 object-contain mx-auto mb-4 animate-pulse"
          />
          <p className="text-muted-foreground font-medium">Loading your book...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={() => navigate(-1)} data-testid="go-back-button">Go Back</Button>
        </div>
      </div>
    );
  }

  // Paywall - Show when user doesn't have access
  if (showPaywall && hasAccess === false) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <img 
            src={KITT.waving} 
            alt="Kitt waving" 
            className="w-40 h-40 object-contain mx-auto mb-6"
          />
          <h1 className="text-2xl font-heading font-bold mb-2">Ready to Start Reading?</h1>
          <p className="text-muted-foreground mb-6">
            Get access to Kitt's Book Nook and start your reading journey with our friendly AI reading companion!
          </p>
          
          {/* Pricing options */}
          <div className="space-y-4 mb-6">
            {/* Beta option - always show for new users */}
            <a
              href="https://sheprompts.gumroad.com/l/KittsBookNook"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full p-4 rounded-xl border-2 border-amber-500 bg-amber-50 dark:bg-amber-950/30 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
              data-testid="beta-purchase-link"
            >
              <div className="font-bold text-amber-700 dark:text-amber-300">90-Day Beta Access</div>
              <div className="text-2xl font-heading font-bold text-amber-600 dark:text-amber-400">$10</div>
              <div className="text-sm text-muted-foreground">Full access for 90 days</div>
            </a>
            
            {/* Foundation Member option - only show to beta users near expiry */}
            {accessInfo?.show_upgrade_option && (
              <div className="space-y-3">
                <a
                  href="https://sheprompts.gumroad.com/l/KBNMembership"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full p-4 rounded-xl border-2 border-purple-500 bg-purple-50 dark:bg-purple-950/30 hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors relative overflow-hidden"
                  data-testid="membership-monthly-link"
                >
                  <div className="absolute top-0 right-0 bg-purple-500 text-white text-xs px-2 py-1 rounded-bl-lg font-bold">
                    BETA EXCLUSIVE
                  </div>
                  <div className="font-bold text-purple-700 dark:text-purple-300">Foundation Member (Monthly)</div>
                  <div className="text-2xl font-heading font-bold text-purple-600 dark:text-purple-400">$5/month</div>
                  <div className="text-sm text-muted-foreground">Lock in this rate forever!</div>
                </a>
                <a
                  href="https://sheprompts.gumroad.com/l/KBNMembership"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full p-4 rounded-xl border-2 border-purple-400 bg-purple-50/50 dark:bg-purple-950/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
                  data-testid="membership-yearly-link"
                >
                  <div className="font-bold text-purple-700 dark:text-purple-300">Foundation Member (Yearly)</div>
                  <div className="text-2xl font-heading font-bold text-purple-600 dark:text-purple-400">$60/year</div>
                  <div className="text-sm text-muted-foreground">Save $5/year!</div>
                </a>
                <p className="text-xs text-center text-muted-foreground">Regular price after beta: $7.50/month</p>
              </div>
            )}
          </div>
          
          {/* License key input */}
          <div className="border-t border-border pt-6 mt-6">
            <p className="text-sm text-muted-foreground mb-3">Already purchased? Enter your license key:</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={licenseKey}
                onChange={(e) => setLicenseKey(e.target.value.toUpperCase())}
                placeholder="XXXX-XXXX-XXXX-XXXX"
                className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-center font-mono text-sm"
                data-testid="license-key-input"
              />
              <Button 
                onClick={verifyLicense}
                disabled={verifyingLicense}
                data-testid="verify-license-button"
              >
                {verifyingLicense ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Activate'}
              </Button>
            </div>
          </div>
          
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)} 
            className="mt-6"
            data-testid="paywall-back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Library
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col overflow-x-hidden" style={{ maxWidth: '100vw' }}>
      {/* Beta expiry warning banner */}
      {accessInfo?.show_upgrade_option && accessInfo?.days_remaining && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-purple-600 to-amber-500 text-white text-center py-2 px-4 text-sm">
          <span className="font-medium">
            Your beta expires in {accessInfo.days_remaining} days!
          </span>
          <a 
            href="https://sheprompts.gumroad.com/l/KBNMembership"
            target="_blank"
            rel="noopener noreferrer"
            className="ml-2 underline font-bold hover:no-underline"
          >
            Lock in $5/month →
          </a>
        </div>
      )}
      
      {/* Header */}
      <header className={`fixed ${accessInfo?.show_upgrade_option ? 'top-10' : 'top-0'} left-0 right-0 z-50 bg-background/95 backdrop-blur-lg border-b border-border transition-all`}>
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => { saveProgress(); logReadingTime(false); navigate(-1); }}
              className="rounded-full"
              data-testid="reader-back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="hidden sm:block">
              <p className="font-heading font-bold text-sm line-clamp-1">{book?.title}</p>
              <p className="text-xs text-muted-foreground">{book?.author}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Band chip */}
            <div className={`hidden sm:flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${bandInfo.color}`}>
              <bandInfo.icon className="w-3 h-3" />
              {bandInfo.name}
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-full"
              data-testid="reader-theme-toggle"
            >
              {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full" data-testid="settings-button">
                  <Settings className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 p-4">
                <DropdownMenuLabel>Reading Settings</DropdownMenuLabel>
                <DropdownMenuSeparator />
                
                {/* Font Size */}
                <div className="py-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm">Font Size</span>
                    <span className="text-sm text-muted-foreground">{fontSize}px</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setFontSize(prev => Math.max(14, prev - 2))}
                      data-testid="font-decrease"
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <Slider
                      value={[fontSize]}
                      onValueChange={([v]) => setFontSize(v)}
                      min={14}
                      max={28}
                      step={2}
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setFontSize(prev => Math.min(28, prev + 2))}
                      data-testid="font-increase"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Line Height */}
                <div className="py-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm">Line Spacing</span>
                    <span className="text-sm text-muted-foreground">{lineHeight.toFixed(1)}</span>
                  </div>
                  <Slider
                    value={[lineHeight]}
                    onValueChange={([v]) => setLineHeight(v)}
                    min={1.4}
                    max={2.4}
                    step={0.2}
                  />
                </div>

                {/* Speech Rate */}
                <div className="py-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm">Reading Speed</span>
                    <span className="text-sm text-muted-foreground">{speechRate.toFixed(1)}x</span>
                  </div>
                  <Slider
                    value={[speechRate]}
                    onValueChange={([v]) => setSpeechRate(v)}
                    min={0.5}
                    max={1.5}
                    step={0.1}
                  />
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Control Bar - FIXED at top, always visible */}
      <div className="fixed top-[57px] left-0 right-0 z-40 bg-background/95 backdrop-blur-lg border-b border-border">
        <div className="px-3 py-2 max-w-screen-sm mx-auto">
          {/* Mode Selector Row */}
          <div className="flex items-center justify-center gap-1 mb-2">
            <button
              onClick={() => { if (modesUnlocked) switchMode(READING_MODES.LISTEN); }}
              disabled={!modesUnlocked}
              className={`flex items-center gap-1 px-2 py-1.5 rounded-full text-xs font-medium transition-all ${
                readingMode === READING_MODES.LISTEN 
                  ? 'bg-blue-500 text-white shadow-md' 
                  : !modesUnlocked
                    ? 'bg-muted text-muted-foreground/50'
                    : 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300'
              }`}
              data-testid="mode-listen"
            >
              <Headphones className="w-3.5 h-3.5" />
              <span className="text-[11px]">Read to Me</span>
            </button>
            
            <button
              onClick={() => switchMode(READING_MODES.TOGETHER)}
              className={`flex items-center gap-1 px-2 py-1.5 rounded-full text-xs font-medium transition-all ${
                readingMode === READING_MODES.TOGETHER 
                  ? 'bg-green-500 text-white shadow-md' 
                  : 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-300'
              }`}
              data-testid="mode-together"
            >
              <Users className="w-3.5 h-3.5" />
              <span className="text-[11px]">Read With Me</span>
            </button>
            
            <button
              onClick={() => { if (modesUnlocked) switchMode(READING_MODES.STUDENT); }}
              disabled={!modesUnlocked}
              className={`flex items-center gap-1 px-2 py-1.5 rounded-full text-xs font-medium transition-all ${
                readingMode === READING_MODES.STUDENT 
                  ? 'bg-amber-500 text-white shadow-md' 
                  : !modesUnlocked
                    ? 'bg-muted text-muted-foreground/50'
                    : 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-300'
              }`}
              data-testid="mode-student"
            >
              <Mic className="w-3.5 h-3.5" />
              <span className="text-[11px]">I'll Read</span>
            </button>
          </div>
          
          {/* Play Controls Row */}
          <div className="flex items-center justify-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={goToPrevious}
              disabled={currentParagraph === 0 && currentSentenceIdx === 0}
              className="rounded-full h-9 w-9 p-0"
              data-testid="prev-paragraph"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>

            <Button
              onClick={togglePlay}
              className={`h-12 w-12 rounded-full shadow-lg transition-all ${
                isListening 
                  ? 'bg-green-500 hover:bg-green-600 text-white animate-pulse' 
                  : isPlaying
                    ? 'bg-amber-500 hover:bg-amber-600 text-white'
                    : 'bg-primary text-primary-foreground hover:bg-primary/90'
              }`}
              data-testid="play-pause-button"
            >
              {isPlaying || isListening ? (
                <Pause className="w-5 h-5" />
              ) : readingMode === READING_MODES.STUDENT ? (
                <Mic className="w-5 h-5" />
              ) : readingMode === READING_MODES.TOGETHER ? (
                <Users className="w-5 h-5" />
              ) : (
                <Play className="w-5 h-5 ml-0.5" />
              )}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={goToNext}
              disabled={currentParagraph === paragraphs.length - 1 && currentSentenceIdx >= getSentences(paragraphs[currentParagraph] || '').length - 1}
              className="rounded-full h-9 w-9 p-0"
              data-testid="next-paragraph"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Unlock progress or listening status */}
          {!modesUnlocked && (
            <p className="text-center text-[10px] text-muted-foreground mt-1">
              {SENTENCES_TO_UNLOCK - togetherSentencesRead} more to unlock all modes
            </p>
          )}
          
          {/* Listening feedback */}
          {isListening && (
            <div className="mt-2 bg-muted rounded-lg p-2">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-xs text-green-600 dark:text-green-400 font-medium">Listening...</span>
                {togetherPhase === 'repeat' && readingMode === READING_MODES.TOGETHER && (
                  <span className="ml-auto text-amber-600 text-xs font-medium">Your turn!</span>
                )}
                <button
                  onClick={() => stt.forceRestart()}
                  className="ml-auto text-xs text-muted-foreground hover:text-foreground px-2 py-0.5 rounded bg-background"
                  title="Restart microphone if not picking up voice"
                >
                  🔄 Restart Mic
                </button>
              </div>
              <p className="text-xs mt-1 truncate">{studentTranscript || <span className="text-muted-foreground italic">Speak now...</span>}</p>
            </div>
          )}
        </div>
      </div>

      {/* Encouragement Popup with Kitt - centered on viewport */}
      {showEncouragement && (
        <div 
          className="fixed z-50 pointer-events-none"
          style={{ 
            top: '50%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)',
            width: '90vw',
            maxWidth: '300px'
          }}
        >
          <div className="bg-gradient-to-br from-green-400 to-green-600 text-white px-6 py-5 rounded-3xl shadow-2xl animate-pop flex flex-col items-center gap-2 mx-auto">
            <img 
              src={KITT.thumbsUp} 
              alt="Kitt giving thumbs up" 
              className="w-16 h-16 object-contain drop-shadow-lg"
            />
            <span className="font-heading font-bold text-lg text-center">{encouragementText}</span>
          </div>
        </div>
      )}

      {/* Mode Unlock Celebration with Kitt - centered on viewport */}
      {showModeUnlockCelebration && (
        <div 
          className="fixed z-50 pointer-events-none"
          style={{ 
            top: '50%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)',
            width: '90vw',
            maxWidth: '320px'
          }}
        >
          <div className="bg-gradient-to-br from-amber-400 to-orange-500 text-white px-6 py-5 rounded-3xl shadow-2xl animate-pop flex flex-col items-center gap-2 mx-auto">
            <img 
              src={KITT.celebrating} 
              alt="Kitt celebrating" 
              className="w-20 h-20 object-contain drop-shadow-lg"
            />
            <span className="font-heading font-bold text-xl text-center">All Modes Unlocked!</span>
            <span className="text-sm opacity-90 text-center">Great job! Now try "Read to Me" or "I'll Read"</span>
          </div>
        </div>
      )}

      {/* Reading Content - mt for fixed header+controls, prevent horizontal scroll */}
      <main 
        ref={contentRef}
        className={`flex-1 w-full px-4 py-4 pb-20 overflow-x-hidden box-border ${accessInfo?.show_upgrade_option ? 'mt-[180px]' : 'mt-[140px]'}`}
      >
        {/* Mobile title */}
        <div className="sm:hidden mb-6">
          <h1 className="font-heading font-bold text-lg break-words">{book?.title}</h1>
          <p className="text-sm text-muted-foreground">{book?.author}</p>
        </div>

        {/* Paragraphs */}
        <div className="space-y-6 w-full">
          {paragraphs.map((para, index) => (
            <div
              key={index}
              ref={el => paragraphRefs.current[index] = el}
              className={`font-reading transition-all duration-300 rounded-xl w-full ${
                index === currentParagraph 
                  ? 'bg-primary/5 px-3 py-4 border-l-4 border-primary shadow-sm' 
                  : 'opacity-50 hover:opacity-70 px-3 py-2'
              }`}
              style={{ 
                fontSize: `${fontSize}px`,
                lineHeight: lineHeight,
                wordWrap: 'break-word',
                overflowWrap: 'break-word'
              }}
              onClick={() => {
                if (index !== currentParagraph) {
                  tts.stop();
                  stt.stop();
                  setIsPlaying(false);
                  setIsListening(false);
                  setCurrentParagraph(index);
                  setCurrentSentenceIdx(0);
                  setWordsRead([]);
                }
              }}
              data-testid={`paragraph-${index}`}
            >
              <p className="text-foreground break-words">
                {renderParagraph(para, index)}
              </p>
            </div>
          ))}
        </div>

        {/* Progress indicator */}
        <div className="mt-12 text-center text-sm text-muted-foreground">
          Paragraph {currentParagraph + 1} of {paragraphs.length}
          {sentencesCompleted > 0 && (
            <span className="ml-2">• {sentencesCompleted} sentences read</span>
          )}
        </div>
      </main>

      {/* Comprehension Dialog */}
      <Dialog open={showComprehension} onOpenChange={setShowComprehension}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              Quick Check-In
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-muted-foreground mb-4">
              Tell me what just happened in your own words. What was this part about?
            </p>
            <Textarea
              placeholder="Write your thoughts here..."
              value={comprehensionResponse}
              onChange={(e) => setComprehensionResponse(e.target.value)}
              className="min-h-[120px] resize-none"
              data-testid="comprehension-input"
            />
          </div>
          <DialogFooter className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => setShowComprehension(false)}
              data-testid="skip-comprehension"
            >
              Skip for now
            </Button>
            <Button onClick={saveComprehension} data-testid="save-comprehension">
              Save & Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
